<template>
  <header
    class="navbar"
    :class="{ 'navbar--scrolled': isScrolled }"
  >
    <span class="navbar__glow" aria-hidden="true"></span>
    <nav class="navbar__container" aria-label="Global">
      <router-link to="/" class="brand__link">
        <img class="brand__logo" :src="logoSrc" alt="Company Logo" />
      </router-link>

      <div class="nav-desktop">
        <router-link
          v-for="item in navigation"
          :key="item.name"
          :to="item.href"
          :class="['nav-link', isActive(item.href) ? 'nav-link--active' : '']"
        >
          {{ item.name }}
        </router-link>
        <!-- <a
          class="primary-button nav-cta"
          href="/contact"
          target="_blank"
        >
          {{ t('nav.contact') }}
        </a> -->
      </div>

      <div class="nav-actions">
        <button
          @click="toggleLanguage"
          class="pill-button text-xs tracking-[0.2em] uppercase"
          aria-label="Toggle Language"
        >
          {{ dataStore.language === 'en' ? 'AR' : 'EN' }}
        </button>
        <button
          @click="toggleDarkModeWithAnimation"
          class="icon-button"
          aria-label="Toggle Dark Mode"
        >
          <template v-if="darkModeStore.isDarkMode">
            <SunIcon class="h-5 w-5" aria-hidden="true" />
          </template>
          <template v-else>
            <MoonIcon class="h-5 w-5" aria-hidden="true" />
          </template>
        </button>
        <button
          type="button"
          @click="toggleMobileMenu"
          class="icon-button icon-button--ghost lg:hidden"
          aria-label="Toggle Mobile Menu"
        >
          <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </nav>

    <transition name="mobile-menu" @after-leave="afterLeave">
      <div
        v-if="mobileMenuOpen"
        class="mobile-overlay lg:hidden"
      >
        <div class="mobile-menu-panel">
          <div class="mobile-menu__header">
            <img class="brand__logo" :src="logoSrc" alt="Company Logo" />
            <button type="button" @click="toggleMobileMenu" aria-label="Close Menu" class="icon-button">
              <XMarkIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div class="mobile-menu__links">
            <router-link
              v-for="item in navigation"
              :key="item.name"
              :to="item.href"
              @click.native="toggleMobileMenu"
              class="mobile-link"
              :class="isActive(item.href) ? 'mobile-link--active' : ''"
            >
              {{ item.name }}
            </router-link>

            <!-- <a
              class="mobile-link mobile-link--accent"
              href="/contact"
              
            >
              {{ t('nav.contact') }}
            </a> -->
          </div>

          <div class="mobile-menu__footer">
            <button
              @click="toggleLanguage"
              class="pill-button uppercase text-sm"
              aria-label="Toggle Language"
            >
              {{ dataStore.language === 'ar' ? 'EN' : 'AR' }}
            </button>
            <button
              @click="toggleDarkModeWithAnimation"
              class="icon-button"
              aria-label="Toggle Dark Mode"
            >
              <template v-if="darkModeStore.isDarkMode">
                <SunIcon class="h-5 w-5" aria-hidden="true" />
              </template>
              <template v-else>
                <MoonIcon class="h-5 w-5" aria-hidden="true" />
              </template>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import { useDarkModeStore } from '@/stores/useDarkModeStore';
import { Bars3Icon, SunIcon, MoonIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { useRoute } from 'vue-router';
import { useDataStore } from '@/stores/useDataStore';
import { useI18n } from 'vue-i18n';
import logoD from '@/assets/LOGO_DARK.png';
import logoL from '@/assets/LOGO_LIGHT.png';

const dataStore = useDataStore();
const darkModeStore = useDarkModeStore();
const route = useRoute();
const { t, locale } = useI18n();

const mobileMenuOpen = ref(false);
const isScrolled = ref(false);

// بيانات القائمة بناءً على الترجمة الحالية
const navigation = computed(() => [
  { name: t('nav.home'), href: '/' },
  { name: t('nav.services'), href: '/service' },
{ name: t('nav.projects'), href: '/projects' },
  { name: t('nav.about'), href: '/about' },
  
  { name: t('nav.contact'), href: '/contact' },
  
  
 
]);

const logoSrc = computed(() => (darkModeStore.isDarkMode ? logoD : logoL));
const isActive = (href) => (href === '/' ? route.path === href : route.path.startsWith(href));
const toggleLanguage = () => {
  const newLanguage = dataStore.language === 'en' ? 'ar' : 'en';
  if (newLanguage) {
    dataStore.setLanguage(newLanguage);
    locale.value = newLanguage;
    updateDirection();
  } else {
    console.error('Invalid language:', newLanguage);
  }
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};
const handleScroll = () => {
  isScrolled.value = window.scrollY > 40;
};
const updateDirection = () => {
  document.documentElement.setAttribute('dir', dataStore.language === 'ar' ? 'rtl' : 'ltr');
};
const toggleDarkModeWithAnimation = () => {
  darkModeStore.toggleDarkMode();
};

// دالة afterLeave
const afterLeave = () => {
  console.log("Mobile menu has closed");
};

onMounted(() => {
  darkModeStore.loadDarkModePreference();
  const storedLanguage = localStorage.getItem('myAppData');
  if (storedLanguage) {
    const parsedLanguage = JSON.parse(storedLanguage);
    if (parsedLanguage.language) {
      dataStore.setLanguage(parsedLanguage.language);
      locale.value = parsedLanguage.language;
      updateDirection();
    }
  } else {
    dataStore.setLanguage('en'); // Default language is English
  }
  dataStore.fetchData();
  updateDirection();
  window.addEventListener('scroll', handleScroll);
});


watch(() => dataStore.language, (newLang, oldLang) => {
  if (newLang !== oldLang) {
    dataStore.fetchData();
    updateDirection();
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>


<style scoped>
.navbar {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 1111;
  padding: 1rem 0;
  transition: background 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease;
}

.navbar--scrolled {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 42px rgba(15, 15, 15, 0.08);
  border-bottom: 1px solid rgba(91, 91, 91, 0.08);
  backdrop-filter: blur(18px);
}

.navbar__glow {
  position: absolute;
  inset: -60px auto auto 45%;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(253, 143, 66, 0.35), transparent 70%);
  filter: blur(60px);
  opacity: 0.6;
  transform: translateX(-50%);
  pointer-events: none;
}

.navbar__container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 auto;
  padding: 0 1.5rem;
  max-width: 1200px;
}

.brand__link {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.brand__logo {
  width: clamp(4.5rem, 8vw, 6.5rem);
  height: auto;
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.08));
}

.nav-desktop {
  display: none;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
  justify-content: center;
}

@media (min-width: 1024px) {
  .nav-desktop {
    display: inline-flex;
  }
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: flex-end;
}

.pill-button {
  padding: 0.55rem 1.5rem;
  border-radius: 999px;
  border: 1px solid rgba(91, 91, 91, 0.35);
  backdrop-filter: blur(12px);
  color: #5b5b5b;
  font-weight: 600;
  letter-spacing: 0.08em;
  transition: all 0.2s ease;
}

.pill-button:hover {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
}

.icon-button {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(91, 91, 91, 0.18);
  color: inherit;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.icon-button--ghost {
  background: rgba(255, 255, 255, 0.4);
  border-color: transparent;
  box-shadow: none;
}

.icon-button:hover {
  transform: translateY(-2px);
  border-color: var(--brand-primary);
}

.nav-cta {
  font-size: 0.9rem;
  padding-inline: 1.75rem;
}

.nav-link {
  position: relative;
  font-weight: 600;
  color: #5b5b5b;
  transition: color 0.2s ease;
}

.nav-link::after {
  content: '';
  position: absolute;
  inset-x: 0;
  bottom: -6px;
  height: 2px;
  background: linear-gradient(120deg, #FD8F42, #ffb37b);
  opacity: 0;
  transform: scaleX(0.5);
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.nav-link:hover,
.nav-link--active {
  color: var(--brand-primary);
}

.nav-link--active::after {
  opacity: 1;
  transform: scaleX(1);
}

.mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 14, 0.65);
  backdrop-filter: blur(6px);
  z-index: 60;
  display: flex;
  justify-content: flex-end;
}

.mobile-menu-panel {
  width: min(180px, 90vw);
  height: 100%;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(16px);
  box-shadow: -20px 0 50px rgba(0, 0, 0, 0.12);
  animation: slide-in 0.35s ease forwards;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.mobile-menu__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mobile-menu__links {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.mobile-link {
  display: block;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  font-size: 1.05rem;
  font-weight: 600;
  background: rgba(91, 91, 91, 0.06);
  color: #1f1f1f;
  transition: background 0.2s ease, color 0.2s ease;
}

.mobile-link--active {
  background: linear-gradient(120deg, #FD8F42, #ffb37b);
  color: #fff;
  box-shadow: 0 14px 35px rgba(253, 143, 66, 0.35);
}

.mobile-link--accent {
  border: none;
  color: #fff;
  background: linear-gradient(140deg, #2f2f2f, #5B5B5B);
}

.mobile-menu__footer {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@keyframes slide-in {
  from {
    transform: translateX(30%);
    opacity: 0;
  }
  to {
    transform: translateX(0%);
    opacity: 1;
  }
}

.mobile-menu-leave-active {
  animation: slide-out 0.35s ease forwards;
}

@keyframes slide-out {
  from {
    transform: translateX(0%);
    opacity: 1;
  }
  to {
    transform: translateX(30%);
    opacity: 0;
  }
}

.dark .navbar--scrolled {
  background: rgba(9, 12, 16, 0.92);
  border-color: rgba(253, 143, 66, 0.15);
}

.dark .nav-link {
  color: rgba(255, 255, 255, 0.75);
}

.dark .icon-button {
  background: rgba(15, 23, 42, 0.75);
  border-color: rgba(253, 143, 66, 0.25);
  color: #f5f5f5;
}

.dark .pill-button {
  color: #f5f5f5;
  border-color: rgba(253, 143, 66, 0.35);
}

.dark .mobile-menu-panel {
  background: rgba(8, 11, 16, 0.95);
  color: #f5f5f5;
}

.dark .mobile-link {
  color: #f5f5f5;
  background: rgba(255, 255, 255, 0.05);
}
/* Add this at the very end of your style section */

/* Light Mode - Color when scrolled */
.mobile-menu-panel--scrolled {
  background: #f0f0f0; /* Change this to your desired scroll color */
  border-left: 1px solid rgba(91, 91, 91, 0.1); /* Optional: adds a border */
}

/* Dark Mode - Color when scrolled */
.dark .mobile-menu-panel--scrolled {
  background: #1a1a1a; /* Change this to your desired dark mode scroll color */
}
</style>
